/**
 * Per-agent voice (Vapi) configuration API service (V4.2 — voice roadmap).
 *
 * Voice is opt-in per agent — absence or `enabled: false` means chat-only.
 * Writes reconcile the live Vapi assistant server-side, so the response
 * reflects both persisted intent and actual provisioning `status`.
 *
 * Pure async functions — no React state, no hooks.
 */
import { get, put } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export interface VoicePolicy {
  resolveFirst: boolean;
  createTicketOnUnresolved: boolean;
  readTicketNumberBack: boolean;
}

export type VoiceConfigStatus =
  | 'draft'
  | 'provisioning'
  | 'active'
  | 'error'
  | 'disabled';

export interface AgentVoiceConfigView {
  agentId: string;
  enabled: boolean;
  status: VoiceConfigStatus;
  vapiAssistantId: string | null;
  voiceProvider: string | null;
  voiceId: string | null;
  firstMessage: string | null;
  phoneNumberId: string | null;
  settings: Record<string, unknown>;
  /** Per-agent voice policy (A6b) — derived server-side from settings.policy. */
  policy?: VoicePolicy;
  updatedAt?: string;
}

export interface UpsertAgentVoiceConfigPayload {
  enabled?: boolean;
  voiceProvider?: string;
  voiceId?: string;
  firstMessage?: string;
  phoneNumberId?: string;
  settings?: Record<string, unknown>;
}

export const agentVoiceConfigService = {
  /** Current voice config for an agent (a default disabled view if never set). */
  get(agentId: string): Promise<AgentVoiceConfigView> {
    return get<AgentVoiceConfigView>(endpoints.agentVoiceConfig.get(agentId));
  },

  /** Upsert config; the response reflects the reconciled provisioning state. */
  upsert(
    agentId: string,
    payload: UpsertAgentVoiceConfigPayload,
  ): Promise<AgentVoiceConfigView> {
    return put<AgentVoiceConfigView>(
      endpoints.agentVoiceConfig.upsert(agentId),
      payload,
    );
  },
} as const;
